import {
  defaultSeparation, parseSeparation, serializeSeparation, isDarkGarment, layoutFilm, defaultWhite,
  type Separation, type SepMode, type WhiteSettings, type ScreenSettings, type FilmSettings, type Rgb,
} from '@eip/engine';
import type { PlateMeta, CheckLite } from '../worker/protocol';
import type { Settings } from './store';

/** The three shapes the app can be in: DTF prep, screen-print separations, or garment mockups. */
export type AppMode = 'dtf' | 'sep' | 'mockup';

/** The shop's last-used separation settings for everything except inks, seeded into
 *  a fresh document (`separationFor`) and persisted across sessions. */
export interface SepDefaults { mode: SepMode; white: WhiteSettings; screen: ScreenSettings; film: FilmSettings; trapPx: number }

/** Per-document separation state: the settings being edited, the worker's most
 *  recent build result, and the generation bookkeeping that keeps a stale build
 *  from clobbering a newer one. */
export interface DocSep {
  separation: Separation;
  plates: PlateMeta[];
  checks: CheckLite[];
  unmatched: number;
  indexSize?: { width: number; height: number };
  building: boolean;
  /** The generation of the most recently REQUESTED build for this doc. */
  buildGeneration?: number;
  /** The generation of the most recently APPLIED (successful) build. */
  builtGeneration?: number;
  /** The `run.generation` the applied build was built from, so the UI can tell
   *  a stale plate preview (the DTF output moved since) from a current one. */
  runGeneration?: number;
  /** Bumped whenever only screening/film layout changed: nothing to rebuild,
   *  but cached film/plate previews must refetch. */
  filmsVersion: number;
  /** A failed BUILD. Blocks every export (`separationReadiness`) — the plates on
   *  screen are from an older build than the settings. */
  error?: string;
  /** A failed films-settings push (`updateSeparation`). Reported next to the
   *  status line, but NOT export-blocking and never written to `error`: a films
   *  push clearing a build error would re-enable exporting the failed build's
   *  plates. */
  filmsError?: string;
  ms?: number;
}

export function defaultSepDefaults(): SepDefaults {
  const d = defaultSeparation('sim', true);
  return { mode: d.mode, white: d.white, screen: d.screen, film: d.film, trapPx: d.trapPx };
}

/** Per-field validation of persisted separation defaults, via `parseSeparation` on a
 *  synthetic envelope (inks are never part of the defaults — every document detects
 *  or edits its own). */
export function sanitizeSepDefaults(raw: unknown): SepDefaults {
  const r = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const mode: SepMode = r.mode === 'sim' || r.mode === 'index' || r.mode === 'spot' ? r.mode : 'sim';
  const envelope = { version: 1, mode, inks: [], white: r.white, screen: r.screen, film: r.film, trapPx: r.trapPx };
  const sep = parseSeparation(JSON.stringify(envelope));
  return { mode: sep.mode, white: sep.white, screen: sep.screen, film: sep.film, trapPx: sep.trapPx };
}

/**
 * A document's own separation if it has one, else a fresh one from the shop
 * defaults with the white settings' garment-dependent fields (`underbase`,
 * `highlight`) recomputed for THIS document's garment — the rest of `white`
 * (strength, floor, choke…) stays whatever the shop last configured.
 */
export function separationFor(doc: DocSep | undefined, defaults: SepDefaults, garment: Rgb): Separation {
  if (doc?.separation) return doc.separation;
  const dark = isDarkGarment(garment);
  const dw = defaultWhite(dark);
  return {
    version: 1,
    mode: defaults.mode,
    inks: [],
    white: { ...defaults.white, underbase: dw.underbase, highlight: dw.highlight },
    screen: defaults.screen,
    film: defaults.film,
    trapPx: defaults.trapPx,
  };
}

/** What a separation change requires: nothing, a plate rebuild (mode/inks/white/trapPx
 *  and `screen.indexDpi` — anything the worker's plate pass reads), or only a film/proof
 *  re-render (the rest of screen/film — purely presentational, the plates themselves are
 *  unaffected).
 *
 *  `screen.indexDpi` looks like a screening setting but index plates are BUILT at it
 *  (`indexPlateSize`), and `renderFilm` throws when a plate's size disagrees with the film
 *  art box — so a bare films push left film view and films export erroring until some
 *  unrelated rebuild. Classified as a rebuild unconditionally (not only in index mode):
 *  the mode can change back, and a redundant rebuild costs one build. */
export type SepChange = 'none' | 'rebuild' | 'films';

export function sepChangeKind(prev: Separation, next: Separation): SepChange {
  if (serializeSeparation(prev) === serializeSeparation(next)) return 'none';
  const core = (s: Separation) => JSON.stringify({ mode: s.mode, inks: s.inks, white: s.white, trapPx: s.trapPx, indexDpi: s.screen.indexDpi });
  return core(prev) === core(next) ? 'films' : 'rebuild';
}

/**
 * The print width a separation builds at: the settings bar's target width, else
 * the NATIVE width of the DTF output the worker actually separates
 * (`run.width / run.dpi`), floored at half an inch.
 *
 * The plan specified `meta.width / settings.dpi`; that was wrong — trim narrows
 * the output, so the source's width stretched the trimmed art across a film art
 * box that was wider than the job.
 *
 * Both callers already hold a `run` (the runner past its `!doc.run` guard, the
 * panel inside its `doc.run` branch); `undefined` only keeps the helper total
 * and falls back to the floor.
 */
export function printWidthFor(settings: Settings, run: { width: number; dpi: number } | undefined): number {
  if (settings.targetWidthIn != null && settings.targetWidthIn > 0) return settings.targetWidthIn;
  const native = run ? run.width / run.dpi : 0;
  return Math.max(0.5, native);
}

/**
 * Index plates are built unrotated (§12); if the configured film would only fit
 * the art by rotating it (or not at all), building would fail deep in the
 * worker. Checked up front so the UI can show the problem before the user
 * clicks "Build".
 */
export function canBuildIndex(sep: Separation, artPx: { width: number; height: number }, printWidthIn: number): string | null {
  try {
    const layout = layoutFilm(artPx, printWidthIn, sep.film, sep.screen.indexDpi);
    if (layout.rotated) {
      return 'This film size only fits the art when rotated; index plates are built unrotated — choose a film size that fits the art without rotating.';
    }
    return null;
  } catch (err) {
    return (err as Error).message;
  }
}
